import React from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import HowBrandWorks from '@rover/components/src/HowBrandWorks';
import appMapShowcaseEnUsSource from '@rover/react-lib/src/images/homepage/localized-app-showcase-images/app-map-showcase-en-us.webp';
import LandingHero, {
  HeroImageSrcSet,
} from '@rover/react-lib/src/pages/landing/components/LandingHero';
import LandingSearchResultsCarousel from '@rover/react-lib/src/pages/landing/components/LandingSearchResultsCarousel';
import QuickFact, {
  QuickFactHighlight,
} from '@rover/react-lib/src/pages/landing/components/QuickFact';
import ServiceBenefits from '@rover/react-lib/src/pages/landing/components/ServiceBenefits';
import ServiceLandingServicesWrapper from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceLandingServicesWrapper';
import Props from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/ServiceLandingPageProps';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import {
  ROVER_DATA_CITATION,
  Superscript,
} from '@rover/react-lib/src/pages/shared/FooterLinks/FactCitations';
import Footer from '@rover/react-lib/src/pages/shared/FooterLinks/Footer';
import { camelize } from '@rover/rsdk/src/modules/Network/object-utilities';
import {
  SERVICE_TYPE_FOR_SERVICE_LANDING_ENUM,
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';

import LandingHelmet from '../../../components/LandingHelmet';
import { getSearchFiltersFromSearchResults } from '../../../utilities';
import servicesLinksImage from '../../components/images/dog-looking-upward.png';
import RoverProtectCondensed from '../../components/RoverProtectCondensed';
import DoggyDayCareServiceFaqs from '../../components/ServiceFaqs/DoggyDayCareServiceFaqs';
import ServiceLandingSection, {
  SERVICE_LANDING_SECTION_MARGIN_Y,
} from '../../components/ServiceLandingSection';
import { SERVICE_SEARCH_FORM_SUBMIT_TYPES } from '../../constants';
import { quickFactStickerChihuahua, quickFactStickerPug } from '../shared-images';

import doggyDayCareHeroImage from './images/doggy-daycare-hero-desktop.jpg';

const heroImages: HeroImageSrcSet = {
  desktop: {
    src: doggyDayCareHeroImage,
    height: 500,
  },
  tablet: {
    src: doggyDayCareHeroImage,
    height: 500,
  },
};

const DoggyDayCare: React.FC<Props> = ({
  countryConfig,
  locale,
  serviceLandingData,
  serviceLinksWithDisplayName,
  searchResults,
  serviceTypes,
  citiesAndNeighborhoodsLinks,
  countryFooterLinks,
  countryRegionLinks,
}) => {
  const { t, i18n } = useLingui();
  const serviceLandingType = ServiceLandingEnum.DOGGY_DAY_CARE;
  const serviceTypeSlug = SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType];
  // Tags analytics events with the real booking service type rather than the SEO-slug vocabulary
  // serviceTypeSlug above uses (e.g. 'overnight-boarding', not 'dog-boarding').
  const internalServiceTypeSlug = SERVICE_TYPE_FOR_SERVICE_LANDING_ENUM[serviceLandingType];

  const { h1, h2, seoTitle, seoMetaDescription } = serviceLandingData;
  const { localizedLinkUrls } = camelize(countryConfig.urls, true);

  const initialSearchFilters = getSearchFiltersFromSearchResults(serviceLandingType, searchResults);

  // This is a temporary quick fact number. Eventually this will be replaced with fact data from the database.
  const NUM_DOGS = 980000;
  const numDogsFormatted = NUM_DOGS.toLocaleString(locale);

  return (
    <>
      <LandingHelmet
        title={seoTitle}
        metaDescription={seoMetaDescription}
        indexable={serviceLandingData.indexable}
      />
      <LandingHero mb="0x" heroImageSrcSet={heroImages} upliftedContent>
        <LandingHero.Content upliftedContent>
          <LandingHero.Display>{h1}</LandingHero.Display>
          <LandingHero.Text>{h2}</LandingHero.Text>
          <LandingHero.SearchForm
            locale={locale}
            serviceTypes={serviceTypes}
            internalServiceType={{ slug: internalServiceTypeSlug }}
            initialSearchFilters={initialSearchFilters}
            showSearchFilters={false}
            submitButtonTextVariant={SERVICE_SEARCH_FORM_SUBMIT_TYPES.DAY_CARE}
          />
        </LandingHero.Content>
      </LandingHero>

      {/* Includes Section within component */}
      <RoverProtectCondensed countryConfig={countryConfig} mb={SERVICE_LANDING_SECTION_MARGIN_Y} />

      {/* Includes Section & margins within component */}
      <ServiceBenefits title={<Trans>A place where your pup can play all day</Trans>}>
        <ServiceBenefits.List>
          <ServiceBenefits.ListItem title={<Trans>Fun & companionship in a local home</Trans>}>
            <Trans>
              Find a sitter who matches your dog’s play, exercise, and enrichment needs. Pick up a
              happy dog at the end of the day!
            </Trans>
          </ServiceBenefits.ListItem>
          <ServiceBenefits.ListItem title={<Trans>Pet sitters you can trust</Trans>}>
            <Trans>
              We make it easy to find 5-star, background-checked sitters. Check out sitter profiles
              and read verified reviews to find a sitter your dog will look forward to staying with.
            </Trans>
          </ServiceBenefits.ListItem>
          <ServiceBenefits.ListItem title={<Trans>Get photos & updates</Trans>}>
            <Trans>
              Find out how your dog is doing by requesting updates in the Rover app. Get photos from
              your sitter of everything from playtime to nap time.
            </Trans>
          </ServiceBenefits.ListItem>
        </ServiceBenefits.List>
      </ServiceBenefits>

      {searchResults && searchResults.results?.length > 0 && (
        <ServiceLandingSection>
          <LandingSearchResultsCarousel
            searchResponse={searchResults}
            searchFilters={initialSearchFilters}
            locale={locale}
            heading={t`Highly-rated doggy day care sitters in your area`}
            headingVariant="display"
            serviceTypeSlug={serviceTypeSlug}
            countryConfig={countryConfig}
          />
        </ServiceLandingSection>
      )}

      {/* Includes Section & margins within component */}
      <QuickFact
        themeColor="yellow"
        stickerImageSrc={quickFactStickerChihuahua}
        my={SERVICE_LANDING_SECTION_MARGIN_Y}
      >
        <Trans>
          Over <QuickFactHighlight>{numDogsFormatted}</QuickFactHighlight> dogs traded couch time
          for play time with Doggy Day Care on Rover
        </Trans>
        <Superscript>1</Superscript>
      </QuickFact>

      <ServiceLandingSection>
        <DoggyDayCareServiceFaqs
          localizedLinkUrls={localizedLinkUrls}
          countryConfig={countryConfig}
          locale={locale}
          searchFilters={initialSearchFilters}
        />
      </ServiceLandingSection>

      {/* Includes Section & margins within component */}
      <QuickFact
        themeColor="pink"
        stickerImageSrc={quickFactStickerPug}
        my={SERVICE_LANDING_SECTION_MARGIN_Y}
      >
        <Trans>
          Safe, supervised dog play can help{' '}
          <QuickFactHighlight>boost your pup's confidence</QuickFactHighlight> in other areas
        </Trans>
        <Superscript>2</Superscript>
      </QuickFact>

      <HowBrandWorks>
        <HowBrandWorks.Display>
          <Trans>How to book doggy day care</Trans>
        </HowBrandWorks.Display>
        <HowBrandWorks.List>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={1}>
              <Trans>Search</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>
                Browse profiles of nearby sitters who offer doggy day care in their homes.
              </Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={2}>
              <Trans>Meet in person</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>Schedule a free in-person Meet & Greet to find the perfect fit.</Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={3}>
              <Trans>Book &amp; pay</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>
                It's that easy! All payments are processed through Rover's secured platform.
              </Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
        </HowBrandWorks.List>
      </HowBrandWorks>

      {/* Includes Section & margins within component */}
      <ServiceLandingServicesWrapper
        serviceLandingType={serviceLandingType}
        serviceLinksWithDisplayName={serviceLinksWithDisplayName}
        imageSrc={servicesLinksImage}
      >
        <Trans>Rover offers many services to meet your pet care needs, including:</Trans>
      </ServiceLandingServicesWrapper>

      <ServiceLandingSection mb={['0x', '0x']} data-qa-id="service-landing-download-app-callout">
        <AppStoreCallout
          altHeading={<Trans>See how much fun your dog is having</Trans>}
          altImageSrc={appMapShowcaseEnUsSource}
        />
      </ServiceLandingSection>

      <Footer
        citiesAndNeighborhoodsLinks={citiesAndNeighborhoodsLinks}
        countryFooterLinks={countryFooterLinks}
        countryRegionLinks={countryRegionLinks}
        citations={[
          i18n._(ROVER_DATA_CITATION),
          <>
            Sommerville, R. et al. (2017).{' '}
            <cite>
              Why do dogs play? Function and welfare implications of play in the domestic dog.
            </cite>{' '}
            Applied Animal Behaviour Science , 197.
          </>,
        ]}
      />
    </>
  );
};

export default DoggyDayCare;
